// Edit-listing dialogs (certificate + sealed dossier). Thin wrappers over the
// shared <ListingDialog> in `mode="edit"` — reused from My Listings, the
// Collection detail popup, and the Marketplace own-listing view so the seller
// can change price / allow_bidding wherever they meet their listing.
//
// The floor-price context is shown the same way the create flow shows it. On
// save, the common caches (my-listings, vault-items, the browse grid, and this
// listing's live-state) are invalidated; a surface can pass `onEdited` for any
// extra refresh it needs.

import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import {
  formatCents,
  SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS,
  SHARE_LISTING_PRICE_MIN_CENTS,
} from "./feeMath";
import type { EditListingResponseData } from "./useEditListing";
import { ListingDialog } from "./ListingDialog";
import {
  useEditSealedDossierListing,
  useEditShareListing,
} from "./useEditListing";
import { useSealedDossierDropFloorPrice } from "./useSealedDossierDropFloorPrice";
import {
  useShareTierFloorPrice,
  type ShareTier,
} from "./useShareTierFloorPrice";

function useCommonInvalidation() {
  const queryClient = useQueryClient();
  return (kind: "share" | "sealed_dossier", listingId: string) => {
    queryClient.invalidateQueries({ queryKey: ["my-listings"] });
    queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    queryClient.invalidateQueries({
      queryKey:
        kind === "share"
          ? ["marketplace-share-listings"]
          : ["marketplace-sealed-dossier-listings"],
    });
    queryClient.invalidateQueries({
      queryKey: ["listing-live-state", kind, listingId],
    });
    // An auto-accepted price drop settles money + open offers.
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    queryClient.invalidateQueries({ queryKey: ["my-offers"] });
  };
}

/** Success toast — distinguishes a plain price update from a price drop that
 *  auto-completed a standing offer. */
function notifyEdited(result: EditListingResponseData) {
  if (result.auto_accepted) {
    const soldFor = formatCents(result.sold_price_cents ?? result.price_cents);
    toast.success(
      `Your price met a standing offer — sold for ${soldFor}.`,
      { duration: 6000 },
    );
  } else {
    toast.success("Listing updated.");
  }
}

export interface EditShareListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  creatorUserId: string;
  tier: ShareTier;
  signed?: boolean;
  priceCents: number;
  allowBidding: boolean;
  /** Extra surface-specific invalidation after a successful save. */
  onEdited?: () => void;
}

export function EditShareListingDialog({
  open,
  onOpenChange,
  listingId,
  creatorUserId,
  tier,
  signed = false,
  priceCents,
  allowBidding,
  onEdited,
}: EditShareListingDialogProps) {
  const floorPriceQuery = useShareTierFloorPrice({
    creatorUserId,
    tier,
    signed,
    enabled: open,
  });
  const mutation = useEditShareListing();
  const invalidate = useCommonInvalidation();

  return (
    <ListingDialog
      open={open}
      onOpenChange={onOpenChange}
      mode="edit"
      initialPriceCents={priceCents}
      initialAllowBidding={allowBidding}
      itemNoun="certificate"
      idPrefix="edit"
      minCents={SHARE_LISTING_PRICE_MIN_CENTS}
      floorScopeLabel="creator + tier"
      alreadyExistsMessage="This certificate already has an active listing."
      floorPriceCents={floorPriceQuery.data?.floor_price_cents}
      isSubmitting={mutation.isPending}
      createListing={(args) =>
        mutation
          .mutateAsync({
            listing_id: listingId,
            price_cents: args.price_cents,
            allow_bidding: args.allow_bidding,
          })
          .then((d) => {
            notifyEdited(d);
            invalidate("share", listingId);
            onEdited?.();
            return { listing_id: d.listing_id };
          })
      }
      onListed={() => {}}
      onAlreadyExists={() => invalidate("share", listingId)}
    />
  );
}

export interface EditSealedDossierListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  dropId: string;
  priceCents: number;
  allowBidding: boolean;
  onEdited?: () => void;
}

export function EditSealedDossierListingDialog({
  open,
  onOpenChange,
  listingId,
  dropId,
  priceCents,
  allowBidding,
  onEdited,
}: EditSealedDossierListingDialogProps) {
  const floorPriceQuery = useSealedDossierDropFloorPrice({
    dropId,
    enabled: open,
  });
  const mutation = useEditSealedDossierListing();
  const invalidate = useCommonInvalidation();

  return (
    <ListingDialog
      open={open}
      onOpenChange={onOpenChange}
      mode="edit"
      initialPriceCents={priceCents}
      initialAllowBidding={allowBidding}
      itemNoun="sealed dossier"
      idPrefix="edit-sd"
      minCents={SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS}
      floorScopeLabel="drop"
      alreadyExistsMessage="This sealed dossier already has an active listing."
      floorPriceCents={floorPriceQuery.data?.floor_price_cents}
      isSubmitting={mutation.isPending}
      createListing={(args) =>
        mutation
          .mutateAsync({
            listing_id: listingId,
            price_cents: args.price_cents,
            allow_bidding: args.allow_bidding,
          })
          .then((d) => {
            notifyEdited(d);
            invalidate("sealed_dossier", listingId);
            onEdited?.();
            return { listing_id: d.listing_id };
          })
      }
      onListed={() => {}}
      onAlreadyExists={() => invalidate("sealed_dossier", listingId)}
    />
  );
}
